import { useTheme } from 'foundations-theming/utils';
import { useCallback, useEffect, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { H4 } from 'foundations-components/transitional/components/H4';
import { useTableColumn } from 'framework-listing-lib/internal/Table/providers/TableColumnsProvider';
import useFetchUserSettings from 'framework-listing-lib/internal/userSettings/hooks/useFetchUserSettings';
import useSaveUserSettings from 'framework-listing-lib/internal/userSettings/hooks/useSaveUserSettings';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { useHasInlineEdit } from 'framework-listing-lib/internal/frameworkAppSettings/hooks/useFrameworkAppSettings';
import useCrmObjectTypeName from 'framework-listing-lib/internal/hooks/useCrmObjectTypeName';
import { useHasActiveOnboardingTour, useHasDisabledAllTours } from 'framework-listing-lib/internal/utils/tourUtils';
import { useIsSelenium } from 'framework-listing-lib/internal/utils/seleniumUtils';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { jsx as _jsx } from "react/jsx-runtime";
function getCellDomElement({
  propertyName,
  rowId
}) {
  return document.querySelector(`tr[data-test-id="crm-table-row"][data-test-object-id="${rowId}"][data-observer-id="0"] td[data-test-property-name="${propertyName}"]`);
}
function useHasDismissedInlineEditingPopover() {
  const objectTypeId = useObjectTypeId();
  const hasInlineEdit = useHasInlineEdit();
  const hasDisabledAllTours = useHasDisabledAllTours();
  const isSelenium = useIsSelenium();
  const settingKey = useMemo(() => hasInlineEdit && !hasDisabledAllTours && !isSelenium ? `HAS_DISMISSED_INLINE_EDITING_POPOVER_${objectTypeId}` : '', [hasDisabledAllTours, hasInlineEdit, isSelenium, objectTypeId]);
  const {
    data
  } = useFetchUserSettings(settingKey, {
    defaultValue: 'false'
  });
  const saveUserSettings = useSaveUserSettings();
  const setter = useCallback(value => {
    saveUserSettings({
      key: settingKey,
      value
    }).catch(() => {});
  }, [saveUserSettings, settingKey]);
  const value = data === undefined ? data : data.value === 'true';
  return [value, setter];
}

/**
 * This component represents a very basic "tour" for inline editing.
 * Rules for this component to be rendered are:
 *
 * - First row on the table => table row with data-observer-id = 0
 * - Target the first editable column => resulting from @inlineEditCell
 * - No active onboarding tour
 * - User has not dismissed the popover yet
 */
export default function InlineEditCellTourPopover(props) {
  const {
    columnName
  } = props;
  const column = useTableColumn(columnName);
  const hasInlineEdit = useHasInlineEdit();
  return hasInlineEdit && column ? /*#__PURE__*/_jsx(InlineEditCellTourPopoverInner, Object.assign({}, props)) : null;
}
function InlineEditCellTourPopoverInner({
  columnName,
  rowId
}) {
  const {
    color
  } = useTheme();
  const hasActiveOnboardingTour = useHasActiveOnboardingTour();
  const {
    objectTypeNamePlural
  } = useCrmObjectTypeName();
  const [hasDismissedInlineEditingPopover, setHasDismissedInlineEditingPopover] = useHasDismissedInlineEditingPopover();
  const shouldRender = useMemo(() => Boolean(!hasActiveOnboardingTour && hasDismissedInlineEditingPopover === false), [hasActiveOnboardingTour, hasDismissedInlineEditingPopover]);
  const handleClose = useCallback(() => {
    setHasDismissedInlineEditingPopover('true');
    const cellElement = getCellDomElement({
      propertyName: columnName,
      rowId
    });
    if (cellElement) {
      cellElement.style.boxShadow = '';
      cellElement.style.transition = '';
    }
  }, [columnName, rowId, setHasDismissedInlineEditingPopover]);
  const handleHighlightCell = useCallback(() => {
    const cellElement = getCellDomElement({
      propertyName: columnName,
      rowId
    });
    if (!cellElement) {
      return;
    }
    cellElement.style.boxShadow = `inset 0 0 0 1px ${color['border-accent-blue-default']}`;
    cellElement.style.transition = 'box-shadow 0.1s ease-in';
    cellElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest'
    });
  }, [columnName, rowId, color]);
  const target = getCellDomElement({
    propertyName: columnName,
    rowId
  });
  useEffect(() => {
    target === null || target === void 0 || target.addEventListener('click', handleClose);
    return () => {
      target === null || target === void 0 || target.removeEventListener('click', handleClose);
    };
  }, [handleClose, target]);
  if (!target) return;
  return /*#__PURE__*/_jsx(Popover, {
    content: {
      header: /*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "frameworkListingLib.inlineEdit.tourPopover.title"
        })
      }),
      body: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "frameworkListingLib.inlineEdit.tourPopover.message",
        options: {
          assetNamePlural: objectTypeNamePlural
        }
      })
    },
    closeOnOutsideClick: true,
    "data-test-id": "inline-edit-tour-popover",
    onOpenChange: handleClose,
    onOpenComplete: handleHighlightCell,
    open: shouldRender,
    showCloseButton: true,
    target: target || undefined,
    use: "shepherd",
    inlineSize: "350px"
  });
}